import React from "react";
import { Plus, X } from "lucide-react";
import { labelCls } from "../../../../theme/styles/classes";
import { ConfiguredDynamicCondition } from "../../../../config/signals";
import { DynamicConditionEditor } from "./signals/DynamicConditionEditor";
import { TickerDropdown } from "../../../../components/common/TickerDropdown";
import styles from "./TradeConditions.module.css";

// Greek condition for options
export interface GreekCondition {
  id: string;
  greek: "delta" | "gamma" | "theta" | "vega" | "rho" | "strike" | "iv" | "Dynamic" | "";
  dynamicGreek?: string;
  greekValue?: number;
}

export interface TradeCondition {
  id: string;
  instrumentType: "Option" | "Stock";
  symbol?: string;
  // Legacy single greek fields (kept for backward compatibility)
  greek?: "delta" | "gamma" | "theta" | "vega" | "rho" | "strike" | "iv" | "Dynamic";
  dynamicGreek?: string;
  greekValue?: number;
  // Multiple greek conditions
  greekConditions?: GreekCondition[];
  quantity?: number;
  direction?: "long" | "short";
  // Dynamic conditions
  dynamicEnabled?: boolean;
  dynamicConditions?: ConfiguredDynamicCondition[];

  // Option-specific fields for API compatibility
  optionType?: "Call" | "Put";
  strike?: number;
  expirationMode?: "expiration" | "dte";
  expiration?: string;
  dte?: number;
  impliedVol?: number;
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
  rho?: number;
}

interface TradeConditionsProps {
  tradeConditions: TradeCondition[];
  onUpdateCondition: (idx: number, field: keyof TradeCondition, value: any) => void;
  legIndex: number;
  hasGlobalSymbols?: boolean;
  intradayEnabled?: boolean;
}

// Helper to generate unique IDs
const generateId = () => `id-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const TradeConditions: React.FC<TradeConditionsProps> = ({
  tradeConditions,
  onUpdateCondition,
  legIndex,
  hasGlobalSymbols = false,
  intradayEnabled = false,
}) => {
  // Add a new greek condition
  const addGreekCondition = (conditionIdx: number) => {
    const condition = tradeConditions[conditionIdx];
    const newGreekCondition: GreekCondition = {
      id: generateId(),
      greek: "",
    };
    const updatedGreekConditions = [...(condition.greekConditions || []), newGreekCondition];
    onUpdateCondition(conditionIdx, 'greekConditions', updatedGreekConditions);
  };

  // Remove a greek condition
  const removeGreekCondition = (conditionIdx: number, greekIdx: number) => {
    const condition = tradeConditions[conditionIdx];
    const updatedGreekConditions = (condition.greekConditions || []).filter((_, i) => i !== greekIdx);
    onUpdateCondition(conditionIdx, 'greekConditions', updatedGreekConditions);
  };

  // Update a greek condition field
  const updateGreekCondition = (conditionIdx: number, greekIdx: number, field: keyof GreekCondition, value: any) => {
    const condition = tradeConditions[conditionIdx];
    const updatedGreekConditions = (condition.greekConditions || []).map((gc, i) =>
      i === greekIdx ? { ...gc, [field]: value } : gc
    );
    onUpdateCondition(conditionIdx, 'greekConditions', updatedGreekConditions);
  };

  // Handle delta value change with Put option logic
  const handleDeltaChange = (conditionIdx: number, greekIdx: number, inputValue: string) => {
    const condition = tradeConditions[conditionIdx];
    const isPut = condition.optionType === "Put";

    // Parse the input value
    let parsedValue = parseFloat(inputValue);

    // If empty or invalid, set to undefined
    if (isNaN(parsedValue) || inputValue === "") {
      updateGreekCondition(conditionIdx, greekIdx, 'greekValue', undefined);
      return;
    }

    // For Put options, always store as negative (backend expects -1 to 0)
    // But display as positive (0 to 1) for user convenience
    if (isPut) {
      // If user enters a negative value, convert to positive for display
      parsedValue = Math.abs(parsedValue);
      // Clamp to 0-1 range
      parsedValue = Math.max(0, Math.min(1, parsedValue));
      // Store as negative
      updateGreekCondition(conditionIdx, greekIdx, 'greekValue', -parsedValue);
    } else {
      // For Call options, clamp to 0-1 range and store as positive
      parsedValue = Math.max(0, Math.min(1, parsedValue));
      updateGreekCondition(conditionIdx, greekIdx, 'greekValue', parsedValue);
    }
  };

  // Get display value for delta (convert negative to positive for Put options)
  const getDeltaDisplayValue = (condition: TradeCondition, greekValue: number | undefined): number | string => {
    if (greekValue === undefined) return "";
    const isPut = condition.optionType === "Put";
    return isPut ? Math.abs(greekValue) : greekValue;
  };

  // Add a new dynamic condition
  const addDynamicCondition = (conditionIdx: number) => {
    const condition = tradeConditions[conditionIdx];
    const newDynamicCondition: ConfiguredDynamicCondition = {
      id: generateId(),
      type: "",
      params: {},
    };
    const updated = [...(condition.dynamicConditions || []), newDynamicCondition];
    onUpdateCondition(conditionIdx, 'dynamicConditions', updated);
  };

  // Remove a dynamic condition
  const removeDynamicCondition = (conditionIdx: number, dynamicIdx: number) => {
    const condition = tradeConditions[conditionIdx];
    const updated = (condition.dynamicConditions || []).filter((_, i) => i !== dynamicIdx);
    onUpdateCondition(conditionIdx, 'dynamicConditions', updated);
  };

  // Update a dynamic condition
  const updateDynamicCondition = (conditionIdx: number, dynamicIdx: number, updatedDc: ConfiguredDynamicCondition) => {
    const condition = tradeConditions[conditionIdx];
    const updated = (condition.dynamicConditions || []).map((dc, i) =>
      i === dynamicIdx ? updatedDc : dc
    );
    onUpdateCondition(conditionIdx, 'dynamicConditions', updated);
  };

  return (
    <div className={styles.container}>
      <label className={labelCls}>Condition Leg {legIndex + 1}</label>
      <div className={styles.conditionsContainer}>
        {tradeConditions.map((condition, i) => (
          <div key={condition.id} className={styles.conditionCard}>
            <div className={styles.fieldGroup}>
              {/* Instrument Type */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Instrument Type</label>
                <select
                  value={intradayEnabled ? "Stock" : condition.instrumentType}
                  onChange={(e) => {
                    if (intradayEnabled) return;
                    onUpdateCondition(i, 'instrumentType', e.target.value);
                  }}
                  className={styles.select}
                >
                  <option value="Stock">Stock</option>
                  {!intradayEnabled && <option value="Option">Option</option>}
                </select>
              </div>

              {/* Option Type - only for Options */}
              {!intradayEnabled && condition.instrumentType === "Option" && (
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Option Type</label>
                  <select
                    value={condition.optionType || ""}
                    onChange={(e) => onUpdateCondition(i, 'optionType', e.target.value || undefined)}
                    className={styles.select}
                  >
                    <option value="">Select Type</option>
                    <option value="Call">Call</option>
                    <option value="Put">Put</option>
                  </select>
                </div>
              )}

              {/* Symbol - only show if no global symbols */}
              {!hasGlobalSymbols && (
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Symbol</label>
                  <TickerDropdown
                    value={condition.symbol || ""}
                    onChange={(symbol) => onUpdateCondition(i, 'symbol', symbol || undefined)}
                    onCommit={(symbol) => onUpdateCondition(i, 'symbol', symbol || undefined)}
                    className={styles.symbolDropdown}
                    placeholder="Select symbol"
                  />
                </div>
              )}

              {/* Greek Conditions - only for Options */}
              {!intradayEnabled && condition.instrumentType === "Option" && (
                <div className={styles.greekConditionsSection}>
                  <div className={styles.greekConditionsHeader}>
                    <label className={styles.fieldLabel}>Greek Conditions</label>
                    <button
                      type="button"
                      onClick={() => addGreekCondition(i)}
                      className={styles.greekAddButton}
                      title="Add Greek Condition"
                    >
                      <Plus size={14} />
                    </button>
                  </div>

                  {(condition.greekConditions || []).map((gc, gIdx) => (
                    <div key={gc.id} className={styles.greekConditionRow}>
                      <select
                        value={gc.greek || ""}
                        onChange={(e) => updateGreekCondition(i, gIdx, 'greek', e.target.value)}
                        className={styles.greekSelect}
                      >
                        <option value="">Select Greek</option>
                        <option value="delta">Delta</option>
                        <option value="gamma">Gamma</option>
                        <option value="theta">Theta</option>
                        <option value="vega">Vega</option>
                        <option value="rho">Rho</option>
                        <option value="strike">Strike</option>
                        <option value="iv">IV</option>
                        <option value="Dynamic">Dynamic</option>
                      </select>

                      {gc.greek && gc.greek !== "Dynamic" && (
                        <input
                          type="number"
                          step={gc.greek === "strike" ? "0.01" : "0.0001"}
                          min={gc.greek === "delta" ? "0" : undefined}
                          max={gc.greek === "delta" ? "1" : undefined}
                          value={gc.greek === "delta" ? getDeltaDisplayValue(condition, gc.greekValue) : (gc.greekValue || "")}
                          onChange={(e) => {
                            if (gc.greek === "delta") {
                              handleDeltaChange(i, gIdx, e.target.value);
                            } else {
                              updateGreekCondition(i, gIdx, 'greekValue', parseFloat(e.target.value) || undefined);
                            }
                          }}
                          className={styles.greekValueInput}
                          placeholder={`${gc.greek} value`}
                        />
                      )}

                      {gc.greek === "Dynamic" && (
                        <input
                          value={gc.dynamicGreek || ""}
                          onChange={(e) => updateGreekCondition(i, gIdx, 'dynamicGreek', e.target.value)}
                          className={styles.greekValueInput}
                          placeholder="Dynamic greek"
                        />
                      )}

                      <button
                        type="button"
                        onClick={() => removeGreekCondition(i, gIdx)}
                        className={styles.greekRemoveButton}
                        title="Remove Greek Condition"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}

                  {(!condition.greekConditions || condition.greekConditions.length === 0) && (
                    <div className={styles.greekEmptyState}>
                      Click + to add a greek condition
                    </div>
                  )}
                </div>
              )}

              {/* Expiration / DTE - only for Options */}
              {!intradayEnabled && condition.instrumentType === "Option" && (
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Expiration / DTE</label>
                  <div className={styles.expirationRow}>
                    <select
                      value={condition.expirationMode || ""}
                      onChange={(e) => {
                        const mode = e.target.value as "expiration" | "dte" | "";
                        onUpdateCondition(i, 'expirationMode', mode || undefined);
                        // Clear the other field when switching modes
                        if (mode === "expiration") {
                          onUpdateCondition(i, 'dte', undefined);
                        } else if (mode === "dte") {
                          onUpdateCondition(i, 'expiration', undefined);
                        }
                      }}
                      className={styles.expirationModeSelect}
                    >
                      <option value="">Select Type</option>
                      <option value="expiration">Expiration Date</option>
                      <option value="dte">DTE (Days to Expiration)</option>
                    </select>

                    {condition.expirationMode === "expiration" && (
                      <input
                        type="date"
                        value={condition.expiration || ""}
                        onChange={(e) => onUpdateCondition(i, 'expiration', e.target.value || undefined)}
                        className={styles.expirationDateInput}
                      />
                    )}

                    {condition.expirationMode === "dte" && (
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={condition.dte ?? ""}
                        onChange={(e) => onUpdateCondition(i, 'dte', e.target.value ? parseInt(e.target.value) : undefined)}
                        className={styles.dteInput}
                        placeholder="Days"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Direction */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Direction</label>
                <select
                  value={condition.direction || ""}
                  onChange={(e) => onUpdateCondition(i, 'direction', e.target.value)}
                  className={styles.select}
                >
                  <option value="">Select Direction</option>
                  <option value="long">Long</option>
                  <option value="short">Short</option>
                </select>
              </div>

              {/* Quantity */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Quantity</label>
                <input
                  type="number"
                  value={condition.quantity || ""}
                  onChange={(e) => onUpdateCondition(i, 'quantity', parseInt(e.target.value) || undefined)}
                  className={styles.input}
                  placeholder="Quantity"
                />
              </div>

              {/* Dynamic Conditions Section */}
              <div className={styles.dynamicSection}>
                <div className={styles.dynamicHeader}>
                  <label className={styles.fieldLabel}>Dynamic</label>
                  <label className={styles.toggleLabel}>
                    <input
                      type="checkbox"
                      checked={condition.dynamicEnabled || false}
                      onChange={(e) => onUpdateCondition(i, 'dynamicEnabled', e.target.checked)}
                      className={styles.toggleInput}
                    />
                    <span className={styles.toggleSwitch} />
                  </label>
                </div>

                {condition.dynamicEnabled && (
                  <div className={styles.dynamicConditionsContainer}>
                    {(condition.dynamicConditions || []).map((dc, dIdx) => (
                      <DynamicConditionEditor
                        key={dc.id}
                        condition={dc}
                        onUpdate={(updatedDc) => updateDynamicCondition(i, dIdx, updatedDc)}
                        onRemove={() => removeDynamicCondition(i, dIdx)}
                        currentLegIndex={legIndex}
                      />
                    ))}

                    <button
                      type="button"
                      onClick={() => addDynamicCondition(i)}
                      className={styles.dynamicAddButton}
                    >
                      <Plus size={14} /> Add Condition
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
